import React from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core';
import Icon from 'sharedComponents/Icon';
import Seo from 'sharedComponents/Seo';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useStyles from './useStyles';

const Faqs = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.container}>
      <Seo title={t('meta.Faqs.title')} />
      <Box className={classes.containerInside}>
        <Typography variant="h1" paragraph>
          Frequently Asked Questions
        </Typography>
        <Typography variant="h2" paragraph>
          General Questions
        </Typography>
        <Box>
          <Accordion square>
            <AccordionSummary expandIcon={<Icon name="expandMore" />} className={classes.summaryBox}>
              <Icon name="questionAnswer" color="primary" className={classes.questionIcon} />
              <Typography> What is Bundle?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="secondary">
                Bundle helps you easily find the best mortgage, anonymously. Other mortgage sites sell your phone number
                to banks. We don&apos;t. You&apos;ll be able to chat with mortgage professionals from different lenders
                and compare mortgage rates. We partner with trusted real estate agents and loan officers that can help
                you buy a home.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion square>
            <AccordionSummary expandIcon={<Icon name="expandMore" />} className={classes.summaryBox}>
              <Icon name="questionAnswer" color="primary" className={classes.questionIcon} />
              <Typography>Why should I use Bundle?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="secondary">
                Bundle is a home buyer-centric platform. We&apos;ve focused on designing a site that we would personally
                want to use to find our mortgage. That means no pushy cold calls. You&apos;ll also have an easy way to
                communicate with multiple lenders, and simple budgeting tools. We pride ourselves in putting you, the
                home buyer, first in everything we
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion square>
            <AccordionSummary expandIcon={<Icon name="expandMore" />} className={classes.summaryBox}>
              <Icon name="questionAnswer" color="primary" className={classes.questionIcon} />
              <Typography>How do I get started?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="secondary">
                To get started on Bundle, sign up for free. After creating your account, you&apos;re off to the races.
                You can anonymously request mortgage rates and message with mortgage loan officers.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
        <br />

        <Typography variant="h2" paragraph>
          Borrower Questions
        </Typography>
        <Box>
          <Accordion square>
            <AccordionSummary expandIcon={<Icon name="expandMore" />} className={classes.summaryBox}>
              <Icon name="questionAnswer" color="primary" className={classes.questionIcon} />
              <Typography>In which states can I use Bundle?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="secondary">
                You can use Bundle in all 50 states. You can view our licensing information on our{' '}
                <Link to="/terms">Terms and Conditions</Link> page.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion square>
            <AccordionSummary expandIcon={<Icon name="expandMore" />} className={classes.summaryBox}>
              <Icon name="questionAnswer" color="primary" className={classes.questionIcon} />
              <Typography>How quickly can I find a mortgage on Bundle?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="secondary">
                You can request mortgage rates and start asking questions as soon as you sign up on Bundle. Once you
                find a loan officer, you can expect the entire mortgage process to take 30 days, from the time you
                submit an application to the day you close on your home. Some lenders can move even faster than that!
                Make sure you ask your loan officer what timeline to expect. Our loan officers typically respond within
                a few minutes during normal business hours. Need more help? Feel free to give us a call.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion square>
            <AccordionSummary expandIcon={<Icon name="expandMore" />} className={classes.summaryBox}>
              <Icon name="questionAnswer" color="primary" className={classes.questionIcon} />
              <Typography>Can Bundle help me refinance?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="secondary">
                Yes, our loan officers can help you refinance the mortgage on your current home.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion square>
            <AccordionSummary expandIcon={<Icon name="expandMore" />} className={classes.summaryBox}>
              <Icon name="questionAnswer" color="primary" className={classes.questionIcon} />
              <Typography>Why does Bundle recommend I do a budget?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="secondary">
                We believe it is important to find out how much home you can afford. Lenders and real estate agents make
                more money when you buy a more expensive house and borrow more money. Lenders may preapprove you for an
                amount that is not right for your individual situation. We want to make sure you are comfortable with
                your monthly mortgage payments, so we help you set a budget that’s personal and comfortable.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion square>
            <AccordionSummary expandIcon={<Icon name="expandMore" />} className={classes.summaryBox}>
              <Icon name="questionAnswer" color="primary" className={classes.questionIcon} />
              <Typography>Do I need 20% for a down payment?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="secondary">
                <em>
                  You can find answers to general mortgage questions like these on our{' '}
                  <a href="https://bundleloan.com/blog/" target="_blank" rel="noopener noreferrer">
                    resources{' '}
                  </a>
                  page.
                </em>{' '}
                <br />
                <br />
                No, you do not need 20% for a down payment. There are many options for financing your home. Many lenders
                offer mortgage products that require as little as 3% down. You will pay more interest if you make a
                smaller down payment. For that reason, Bundle recommends you aim for a 10% down payment. On Bundle, you
                can shop and compare multiple options from different lenders. Our loan officers are here to help you
                answer questions like these. Create a free account to start chatting with trusted mortgage
                professionals.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
        <br />
        <Typography variant="h2" paragraph>
          Real Estate Agent Questions
        </Typography>
        <Box>
          <Accordion square>
            <AccordionSummary expandIcon={<Icon name="expandMore" />} className={classes.summaryBox}>
              <Icon name="questionAnswer" color="primary" className={classes.questionIcon} />
              <Typography>Why should I use Bundle as a real estate agent?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="secondary">
                Bundle allows you to help your home buyers through the mortgage process. We provide real estate agents
                with free tools to educate your clients. Your home buyer will be able to easily connect with your
                <em> favorite loan officers</em> all on the same platform. We make your job easier and provide an
                incredible experience for your clients so you can grow your referral business.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion square>
            <AccordionSummary expandIcon={<Icon name="expandMore" />} className={classes.summaryBox}>
              <Icon name="questionAnswer" color="primary" className={classes.questionIcon} />
              <Typography>How much does Bundle cost?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="secondary">
                Bundle is always free for real estate agents and home buyers. Bundle is also free for loan officers when
                a home buyer comes through a real estate agent. Bundle makes money by connecting loan officers to home
                buyers that come to Bundle outside of our real estate agent network.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion square>
            <AccordionSummary expandIcon={<Icon name="expandMore" />} className={classes.summaryBox}>
              <Icon name="questionAnswer" color="primary" className={classes.questionIcon} />
              <Typography>How do I manage my account?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="secondary">
                From the home page, you can click your name in the top-right corner and select <em>Settings</em>. Here
                is a quick link to get there: <Link to="/real-estate-agent/account-settings">account settings</Link>
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        <br />
        <Typography variant="h2" paragraph>
          Mortgage Loan Officers
        </Typography>
        <Box>
          <Accordion square>
            <AccordionSummary expandIcon={<Icon name="expandMore" />} className={classes.summaryBox}>
              <Icon name="questionAnswer" color="primary" className={classes.questionIcon} />
              <Typography>How much does it cost?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="secondary">
                Bundle is always free for real estate agents and home buyers. Bundle is also free for loan officers when
                a home buyer comes through a real estate agent. Bundle makes money by connecting loan officers to home
                buyers (typically $25-$50 per connection) that come to Bundle outside of our real estate agent network.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion square>
            <AccordionSummary expandIcon={<Icon name="expandMore" />} className={classes.summaryBox}>
              <Icon name="questionAnswer" color="primary" className={classes.questionIcon} />
              <Typography>How does Bundle match me with homebuyers?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="secondary">
                Our proprietary algorithm matches you homebuyers automatically. The primary factor is the location of
                the house and your licensed states.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion square>
            <AccordionSummary expandIcon={<Icon name="expandMore" />} className={classes.summaryBox}>
              <Icon name="questionAnswer" color="primary" className={classes.questionIcon} />
              <Typography>What if I use real estate agent referrals?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="secondary">
                Real estate agent referrals are free for loan officers on Bundle. Real estate agents use Bundle to help
                give their clients navigate the mortgage process. Do you have a real estate agent you think is a great
                fit for Bundle? You can easily refer them in our account settings.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    </Box>
  );
};

export default Faqs;



// WEBPACK FOOTER //
// ./src/components/Faqs/Faqs.js